import { TGetItemFn, TRemoveItemFn, TSetItemFn, TSetLastVisitedItemFn } from '@/config/types'
import { PageName } from '@/enums/pageName'
import { CheckoutFlowVersion } from '@/enums/payments'
import { getCookie, setCookie } from '@/utils/getCookieUtils'
import { getPageName, getPath } from '@/utils/urlUtils'
import { uuidv4 } from '@/utils/uuid'

import { contactIdKey, emailKey, tokenKey } from '@/auth/types'
import { trackSessionStart } from '@/events/events'
import { FBPixel } from '@/events/fb-pixel'
import { isLeadEventSent, markLeadEventSent } from '@/events/utils'

import { FBParams, GAParams, ISession, TikTokParams, TWParams } from './types'
import {
  getParamsFromQuery,
  SecondaryUTMParams,
  TURLParams,
  TURLParamsData,
  UTMParams,
} from './urlManagement'

class LocalStorageAreNotReadyError extends Error {
  constructor(message?: string) {
    super(message)
    this.name = 'LocalStorageAreNotReadyError'
  }
}

export const secondaryUtmParamsMapping: Record<SecondaryUTMParams, string> = {
  [SecondaryUTMParams.source]: 'sec_source',
  [SecondaryUTMParams.onboardId]: 'sec_id',
  [SecondaryUTMParams.test_event_code]: 'test_event_code',
} as const

function saveFirstVisit(
  getItem: TGetItemFn,
  setItem: TSetItemFn,
  firstVisitAtFromURL: string | undefined,
  millisecondsSinceFirstVisitFromURL: string | undefined,
  newParams: boolean
): [Date, boolean] {
  let firstVisitAt: Date | undefined
  const firstVisitAtFromLS = getItem('firstVisitAt')

  if (firstVisitAtFromURL) {
    const timestamp = Date.parse(firstVisitAtFromURL)
    if (!isNaN(timestamp)) {
      firstVisitAt = new Date(timestamp)
      const isoStr = firstVisitAt.toISOString()
      setItem('firstVisitAt', isoStr)
      return [firstVisitAt, isoStr !== firstVisitAtFromLS]
    }
  } else if (millisecondsSinceFirstVisitFromURL) {
    const milliseconds = Number(millisecondsSinceFirstVisitFromURL)
    if (!isNaN(milliseconds)) {
      firstVisitAt = new Date(new Date().getTime() - milliseconds)
      const isoStr = firstVisitAt.toISOString()
      setItem('firstVisitAt', isoStr)
      return [firstVisitAt, true]
    }
  }
  if (!firstVisitAt && firstVisitAtFromLS) {
    firstVisitAt = new Date(firstVisitAtFromLS)
  }
  if (!firstVisitAt || newParams) {
    firstVisitAt = new Date()
    setItem('firstVisitAt', firstVisitAt.toISOString())
    console.log('new visit', firstVisitAt)
  }

  return [firstVisitAt, firstVisitAtFromLS !== firstVisitAt.toISOString()]
}

function enrichParamsWithUrlParams(
  urlParams: TURLParamsData,
  getItem: TGetItemFn,
  setItem: TSetItemFn
): [Record<string, string | undefined>, boolean] {
  const paramsData = getItem('params') || ''
  const parsedParams: Record<string, string | undefined> =
    (paramsData && JSON.parse(paramsData)) || {}

  const paramNamesList: TURLParams[] = [
    ...Object.values(UTMParams),
    ...Object.values(SecondaryUTMParams),
  ]

  for (const paramName of paramNamesList) {
    const value = urlParams[paramName]
    if (!value) {
      continue
    }
    let key: string = paramName
    if (paramName in secondaryUtmParamsMapping) {
      key = secondaryUtmParamsMapping[paramName as SecondaryUTMParams]
    }
    parsedParams[key] = value
  }

  const newParamsStr = JSON.stringify(parsedParams)
  setItem('params', newParamsStr)

  return [parsedParams, newParamsStr !== paramsData]
}

function saveAdNetworksData(
  urlParams: TURLParamsData,
  getItem: TGetItemFn,
  setItem: TSetItemFn,
  removeItem: TRemoveItemFn
): [FBParams | undefined, GAParams | undefined, TWParams | undefined, TikTokParams | undefined] {
  let fb_user_data: FBParams | undefined
  let google_user_data: GAParams | undefined
  let twitter_user_data: TWParams | undefined
  let tiktok_user_data: TikTokParams | undefined

  if (urlParams.twclid) {
    twitter_user_data = {
      twclid: urlParams.twclid,
    }
  } else if (urlParams.ttclid) {
    tiktok_user_data = {
      ttclid: urlParams.ttclid,
    }
  } else if (urlParams.fbc && urlParams.fbp) {
    fb_user_data = {
      fbc: urlParams.fbc,
      fbp: urlParams.fbp,
    }
  } else if (urlParams.fbclid) {
    const ts = new Date().getTime()
    fb_user_data = {
      fbc: `fb.1.${ts}.${urlParams.fbclid}`,
      fbp: `fb.1.${ts}.${Math.round(2147483648 * Math.random())}`,
    }
  } else if (urlParams.gclid || urlParams.wbraid || urlParams.gbraid) {
    google_user_data = {
      gclid: urlParams.gclid,
      wbraid: urlParams.wbraid,
      gbraid: urlParams.gbraid,
    }
  } else {
    const fb_str = getItem('fb_user_data')
    const ga_str = getItem('google_user_data')
    const tw_str = getItem('twitter_user_data')
    const tt_str = getItem('tiktok_user_data')
    if (fb_str) {
      fb_user_data = JSON.parse(fb_str)
    } else if (ga_str) {
      google_user_data = JSON.parse(ga_str)
    } else if (tw_str) {
      twitter_user_data = JSON.parse(tw_str)
    } else if (tt_str) {
      tiktok_user_data = JSON.parse(tt_str)
    }
  }

  if (fb_user_data) {
    setItem('fb_user_data', JSON.stringify(fb_user_data))
    removeItem('google_user_data')
    removeItem('twitter_user_data')
    removeItem('tiktok_user_data')
  } else if (google_user_data) {
    removeItem('fb_user_data')
    removeItem('twitter_user_data')
    removeItem('tiktok_user_data')
    setItem('google_user_data', JSON.stringify(google_user_data))
  } else if (tiktok_user_data) {
    removeItem('fb_user_data')
    removeItem('twitter_user_data')
    removeItem('google_user_data')
    setItem('tiktok_user_data', JSON.stringify(tiktok_user_data))
  } else if (twitter_user_data) {
    removeItem('fb_user_data')
    removeItem('google_user_data')
    removeItem('tiktok_user_data')
    setItem('twitter_user_data', JSON.stringify(twitter_user_data))
  }

  return [fb_user_data, google_user_data, twitter_user_data, tiktok_user_data]
}

function saveSessionFact(exists: boolean, experimentName: string): void {
  setCookie('sidExists', exists ? 1 : 0, {
    domain: window.location.hostname,
    'max-age': exists ? 3600 * 24 * 7 * 3 : 0, //3 weeks
    path: `/${experimentName}`,
    samesite: 'lax',
  })
}

function saveSessionParams(
  urlParams: TURLParamsData,
  getItem: TGetItemFn,
  setItem: TSetItemFn,
  updatedParams: boolean,
  experimentName: string
): [string, Date, boolean, boolean] {
  const [firstVisitAt, updated] = saveFirstVisit(
    getItem,
    setItem,
    urlParams.fva,
    urlParams.mfva,
    updatedParams
  )

  if (urlParams.token && urlParams.contact_id) {
    setItem(tokenKey, urlParams.token)
    setItem(contactIdKey, urlParams.contact_id)
    setItem(emailKey, urlParams.email)
  }
  let sessionId: string | undefined
  let freshSession = false

  if (urlParams.sid) {
    sessionId = urlParams.sid
    setItem('sessionId', sessionId)
  }

  if (urlParams.app_country_tier) {
    setItem('appCountryTier', urlParams.app_country_tier)
  }

  if (!sessionId) {
    sessionId = getItem('sessionId') || undefined
  }
  if (!sessionId) {
    if (getCookie('sidExists')) {
      throw new LocalStorageAreNotReadyError()
    }

    sessionId = uuidv4()
    setItem('sessionId', sessionId)

    freshSession = true
  }

  saveSessionFact(true, experimentName)

  return [sessionId, firstVisitAt, updated, freshSession]
}

function initFBPixel(sessionId: string, fbParams: FBParams | undefined): void {
  // every time we have our own fbc we set cookie
  // to avoid mismatch between our fbc and the one generated by fb pixel
  //
  let domainParts = window.location.hostname.split('.')
  if (domainParts.length > 2) {
    domainParts = domainParts.slice(-2)
  }
  const domain = '.' + domainParts.join('.')

  const cookieOptions = {
    domain,
    'max-age': 3600 * 24 * 90, // 3 month
    path: '/',
    samesite: 'lax',
  }
  if (fbParams?.fbc) {
    setCookie('_fbc', fbParams?.fbc, cookieOptions)
    setCookie('_fbp', fbParams?.fbp, cookieOptions)
  } else {
    cookieOptions['max-age'] = 0 // cookie will be removed
    setCookie('_fbc', '', cookieOptions)
    setCookie('_fbp', '', cookieOptions)
  }

  FBPixel.init(window.config.PIXEL_ID, sessionId)
}

function replaceState(
  urlParams: TURLParamsData,
  pagesOrder: PageName[],
  sessionId: string,
  firstVisitAt: Date,
  updatedFVA: boolean,
  freshSession: boolean,
  fbParams: FBParams | undefined,
  gaParams: GAParams | undefined,
  twParams: TWParams | undefined,
  tikTokParams: TikTokParams | undefined
) {
  const currentUrl: URL = new URL(window.location.href)
  // TODO: update fbc&fbp if exist
  const urlFBP = currentUrl.searchParams.get('fbp')
  const urlFBC = currentUrl.searchParams.get('fbc')
  const urlTWClid = currentUrl.searchParams.get('twclid')
  const urlTTClid = currentUrl.searchParams.get('ttclid')

  const urlGclid = currentUrl.searchParams.get('gclid') || undefined
  const urlGbraid = currentUrl.searchParams.get('gbraid') || undefined
  const urlWbraid = currentUrl.searchParams.get('wbraid') || undefined

  const emailCampaign = currentUrl.searchParams.get(UTMParams.utm_email_campaign) || undefined
  const ltpEmailCampaign =
    currentUrl.searchParams.get(UTMParams.utm_ltp_email_campaign) || undefined

  const emailCampaingChanged = emailCampaign !== urlParams.utm_email_campaign
  const ltpEmailCampaingChanged = ltpEmailCampaign !== urlParams.utm_ltp_email_campaign

  const isFBParamsChanged =
    (urlFBP || urlFBC) &&
    fbParams?.fbp &&
    fbParams?.fbc &&
    (urlFBP !== fbParams.fbp || urlFBC != fbParams.fbc)

  const isTWParamsChanged = urlTWClid && twParams?.twclid && urlTWClid !== twParams.twclid
  const isTikTokParamsChanged =
    urlTTClid && tikTokParams?.ttclid && urlTTClid !== tikTokParams.ttclid

  const isGAParamsChanged =
    (urlGclid || urlGbraid || urlWbraid) &&
    (urlGclid !== gaParams?.gclid || urlGbraid != gaParams?.gbraid || urlWbraid != gaParams?.wbraid)

  if (
    !(
      freshSession ||
      urlParams.token ||
      updatedFVA ||
      !currentUrl.searchParams.get('sid') ||
      !currentUrl.searchParams.get('fva') ||
      isFBParamsChanged ||
      isGAParamsChanged ||
      currentUrl.searchParams.get('token') ||
      isTWParamsChanged ||
      isTikTokParamsChanged ||
      emailCampaingChanged ||
      ltpEmailCampaingChanged
    )
  ) {
    return
  }

  if (isTWParamsChanged && twParams?.twclid) {
    currentUrl.searchParams.set('twclid', twParams.twclid)
  }
  if (isTikTokParamsChanged && tikTokParams?.ttclid) {
    currentUrl.searchParams.set('ttclid', tikTokParams.ttclid)
  }

  if (urlParams.token || freshSession || !currentUrl.searchParams.get('sid')) {
    currentUrl.searchParams.set('sid', sessionId)
  }
  if (updatedFVA || !currentUrl.searchParams.get('fva')) {
    currentUrl.searchParams.set('fva', firstVisitAt.toISOString())
  }

  if (isFBParamsChanged) {
    currentUrl.searchParams.set('fbp', fbParams.fbp)
    currentUrl.searchParams.set('fbc', fbParams.fbc)
  }

  if (isGAParamsChanged) {
    if (gaParams?.gclid) {
      currentUrl.searchParams.set('gclid', gaParams.gclid)
    }
    if (gaParams?.gbraid) {
      currentUrl.searchParams.set('gbraid', gaParams.gbraid)
    }
    if (gaParams?.wbraid) {
      currentUrl.searchParams.set('wbraid', gaParams.wbraid)
    }
  }

  if (currentUrl.searchParams.get('token')) {
    currentUrl.searchParams.delete('token')
  }

  if (emailCampaingChanged) {
    currentUrl.searchParams.delete(UTMParams.utm_email_campaign)
    urlParams.utm_email_campaign &&
      currentUrl.searchParams.set(UTMParams.utm_email_campaign, urlParams.utm_email_campaign)
  }

  if (ltpEmailCampaingChanged) {
    currentUrl.searchParams.delete(UTMParams.utm_ltp_email_campaign)
    urlParams.utm_ltp_email_campaign &&
      currentUrl.searchParams.set(
        UTMParams.utm_ltp_email_campaign,
        urlParams.utm_ltp_email_campaign
      )
  }

  let hash = currentUrl.hash

  hash = hash.split('?')[0]
  if (urlParams.token) {
    const currentPageName = getPageName(hash)
    const isLTPPage = currentPageName.startsWith('ltp')
    const selectLTPIndex = pagesOrder.indexOf(PageName.selectLTP)

    if (selectLTPIndex !== -1 && isLTPPage) {
      if (pagesOrder.indexOf(currentPageName) < pagesOrder.indexOf(PageName.selectLTP)) {
        hash = '#' + getPath(PageName.selectLTP)
      }
    } else if (pagesOrder.indexOf(currentPageName) < pagesOrder.indexOf(PageName.selectPlan)) {
      hash = '#' + getPath(PageName.selectPlan)
    }
  }

  window.history.replaceState(
    window.history.state,
    '',
    currentUrl.pathname + currentUrl.search + hash // + currentUrl.hash
  )
}

function dropSession(): void {
  localStorage.clear()

  const currentUrl: URL = new URL(window.location.href)

  currentUrl.searchParams.delete('drop_session')
  currentUrl.hash = ''

  window.location.href = currentUrl.href
}

export function _initSessionData(
  getItem: TGetItemFn,
  setItem: TSetItemFn,
  removeItem: TRemoveItemFn,
  pagesOrder: PageName[],
  experimentName: string,
  setLastVisitedItem: TSetLastVisitedItemFn,
  forceExtraDiscountPrice: boolean = false,
  checkoutFlowVersion?: CheckoutFlowVersion
): ISession {
  checkoutFlowVersion = checkoutFlowVersion || CheckoutFlowVersion.V1
  const urlParams = getParamsFromQuery()

  if (urlParams.drop_session) {
    dropSession()
    throw Error('Session has been dropped. Page will be reloaded')
  }

  const [fbParams, gaParams, twParams, tikTokParams] = saveAdNetworksData(
    urlParams,
    getItem,
    setItem,
    removeItem
  )

  if (urlParams.fbclid) {
    delete urlParams.utm_email_campaign
    delete urlParams.utm_ltp_email_campaign
  }

  const [utmParams, updatedParams] = enrichParamsWithUrlParams(urlParams, getItem, setItem)

  const [sessionId, fva, updatedFVA, freshSession] = saveSessionParams(
    urlParams,
    getItem,
    setItem,
    updatedParams,
    experimentName
  )

  const isAnalyticsEnabled = urlParams.disable_analytics ? false : true

  if (isAnalyticsEnabled) {
    initFBPixel(sessionId, fbParams)
  } else {
    console.log('analytics disabled')
  }

  if (freshSession) {
    trackSessionStart(window.config.PARTNER_ID, window.config.APP_ID, window.config.PAGE_SET_ID, {
      sessionId,
      fbParams,
      gaParams,
      twParams,
      tikTokParams,
      utmParams,
      token: urlParams.token,
      fva,
      isAnalyticsEnabled,
      checkoutFlowVersion,
      forceExtraDiscountPrice,
    })
  }

  if (urlParams.token) {
    setLastVisitedItem(PageName.selectPlan)
  }

  if (urlParams.plan_id) {
    const planId = urlParams.plan_id
    setItem(PageName.selectPlan, planId)
    if (checkoutFlowVersion === CheckoutFlowVersion.V2) {
      // in v2 we don't need token to fast forward to select-plan
      setLastVisitedItem(PageName.selectPlan)
    }

    // existing plan means that experiment was opened by email link or heyflow
    // so we don't need to send Lead again
    if (checkoutFlowVersion != CheckoutFlowVersion.V1) {
      if (!isLeadEventSent(getItem)) {
        markLeadEventSent(setItem)
      }
    }
  }

  if (urlParams.content_price_id) {
    const contentPriceId = urlParams.content_price_id
    setItem(PageName.selectLTP, contentPriceId)

    if (urlParams.token) {
      setLastVisitedItem(PageName.selectLTP)
    }
  }
  replaceState(
    urlParams,
    pagesOrder,
    sessionId,
    fva,
    updatedFVA,
    freshSession,
    fbParams,
    gaParams,
    twParams,
    tikTokParams
  )

  if (urlParams.show_storage) {
    let info = ''
    Object.keys(localStorage)
      .sort()
      .forEach((key) => {
        info += `${key}: ${localStorage[key]}\n`
      })
    alert(info)
  }
  return {
    sessionId,
    fbParams,
    gaParams,
    twParams,
    tikTokParams,
    utmParams,
    isAnalyticsEnabled,
    token: urlParams.token,
    fva,
    checkoutFlowVersion,
    forceExtraDiscountPrice,
  }
}

/**
 * @function
 * @param {boolean} [forceExtraDiscountPrice=false] - will be used on select plan page
 *     to *always* show extra disctount plans.
 *     Usually it should be 'true' for standalone experiments
 */
export function initSessionData(
  getItem: TGetItemFn,
  setItem: TSetItemFn,
  removeItem: TRemoveItemFn,
  pagesOrder: PageName[],
  experimentName: string,
  setLastVisitedItem: TSetLastVisitedItemFn,
  forceExtraDiscountPrice: boolean = false,
  checkoutFlowVersion: CheckoutFlowVersion = CheckoutFlowVersion.V1
): Promise<ISession> {
  return new Promise((resolve, reject) => {
    let attemptsCount = 0
    const intervalId = setInterval(() => {
      attemptsCount += 1

      if (attemptsCount >= 10) {
        clearInterval(intervalId)
        saveSessionFact(false, experimentName)
      }
      try {
        resolve(
          _initSessionData(
            getItem,
            setItem,
            removeItem,
            pagesOrder,
            experimentName,
            setLastVisitedItem,
            forceExtraDiscountPrice,
            checkoutFlowVersion
          )
        )
        clearInterval(intervalId)
      } catch (err) {
        if (err instanceof LocalStorageAreNotReadyError) {
          return // another attempt
        }
        console.log('err', err)
        reject(err)
      }
    }, 100)
  })
}
